import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Package, Wrench, User, LogOut, ShoppingBag, Calendar, ChevronRight } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerDashboardPage = () => {
    const { user, logout, getAuthHeaders, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !user) {
            navigate("/login", { replace: true });
        }
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (!user) return;
        const fetchData = async () => {
            try {
                const headers = getAuthHeaders();
                const [ordersRes, bookingsRes] = await Promise.all([
                    axios.get(`${API_BASE}/customer/orders`, { headers }),
                    axios.get(`${API_BASE}/customer/bookings`, { headers })
                ]);
                setOrders(ordersRes.data);
                setBookings(bookingsRes.data);
            } catch (err) {
                console.error("Failed to load data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user, getAuthHeaders]);

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    };

    const statusColor = (status) => {
        const colors = {
            pending: "bg-yellow-500/20 text-yellow-500",
            pending_payment: "bg-yellow-500/20 text-yellow-500",
            verified: "bg-blue-500/20 text-blue-500",
            dispatch: "bg-purple-500/20 text-purple-500",
            shipped: "bg-green-500/20 text-green-500",
            confirmed: "bg-green-500/20 text-green-500",
            completed: "bg-green-500/20 text-green-500",
            cancelled: "bg-red-500/20 text-red-500",
        };
        return colors[status] || "bg-secondary text-muted-foreground";
    };

    if (authLoading) {
        return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
    }

    if (!user) return null;

    return (
        <div className="container mx-auto px-6 lg:px-12 py-12" data-testid="customer-dashboard">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-heading font-black text-3xl tracking-tight">
                        MY <span className="text-primary">ACCOUNT</span>
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Welcome back, {user.first_name}
                    </p>
                </div>
                <Button variant="outline" className="rounded-none gap-2" onClick={handleLogout} data-testid="customer-logout-btn">
                    <LogOut className="h-4 w-4" /> Sign Out
                </Button>
            </div>

            {/* Account Info */}
            <Card className="rounded-none border-border mb-8">
                <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary/10 flex items-center justify-center">
                            <User className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                            <p className="font-heading font-bold">{user.first_name} {user.last_name}</p>
                            <p className="text-muted-foreground text-sm">{user.email}</p>
                            {user.company && <p className="text-muted-foreground text-xs">{user.company}</p>}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="orders">
                <TabsList className="rounded-none bg-card border border-border w-full justify-start">
                    <TabsTrigger value="orders" className="rounded-none gap-2 data-[state=active]:bg-primary/10" data-testid="tab-orders">
                        <ShoppingBag className="h-4 w-4" /> Orders ({orders.length})
                    </TabsTrigger>
                    <TabsTrigger value="rentals" className="rounded-none gap-2 data-[state=active]:bg-primary/10" data-testid="tab-rentals">
                        <Wrench className="h-4 w-4" /> Rentals ({bookings.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="orders" className="mt-6">
                    {loading ? (
                        <div className="space-y-4">
                            {[1,2].map(i => <div key={i} className="h-24 bg-card border border-border animate-pulse" />)}
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="text-center py-16 bg-card border border-border">
                            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground mb-4">No orders yet</p>
                            <Link to="/products">
                                <Button className="rounded-none" data-testid="browse-equipment-btn">Browse Equipment</Button>
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {orders.map(order => (
                                <Card key={order.id} className="rounded-none border-border" data-testid={`order-card-${order.order_number}`}>
                                    <CardContent className="p-6">
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                            <div>
                                                <div className="flex items-center gap-3 mb-1">
                                                    <p className="font-heading font-bold">{order.order_number}</p>
                                                    <Badge className={`rounded-none text-xs ${statusColor(order.status)}`}>
                                                        {(order.status || "pending").replace(/_/g, " ").toUpperCase()}
                                                    </Badge>
                                                </div>
                                                <p className="text-muted-foreground text-sm flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" /> {formatDate(order.created_at)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-mono text-lg font-bold text-primary">${(order.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                                                <p className="text-xs text-muted-foreground">{order.payment_method?.replace(/_/g, " ").toUpperCase()}</p>
                                            </div>
                                        </div>
                                        <div className="mt-4 border-t border-border pt-3">
                                            <p className="text-sm text-muted-foreground">
                                                {order.items?.map(i => `${i.product_name} (x${i.quantity})`).join(", ")}
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="rentals" className="mt-6">
                    {loading ? (
                        <div className="space-y-4">
                            {[1,2].map(i => <div key={i} className="h-24 bg-card border border-border animate-pulse" />)}
                        </div>
                    ) : bookings.length === 0 ? (
                        <div className="text-center py-16 bg-card border border-border">
                            <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground mb-4">No rental bookings yet</p>
                            <Link to="/rent">
                                <Button className="rounded-none" data-testid="browse-rentals-btn">Browse Rental Fleet</Button>
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {bookings.map(booking => (
                                <Card key={booking.id} className="rounded-none border-border" data-testid={`booking-card-${booking.id}`}>
                                    <CardContent className="p-6">
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                            <div>
                                                <div className="flex items-center gap-3 mb-1">
                                                    <p className="font-heading font-bold">{booking.machine_model || "Machine Rental"}</p>
                                                    <Badge className={`rounded-none text-xs ${statusColor(booking.status)}`}>
                                                        {(booking.status || "pending").toUpperCase()}
                                                    </Badge>
                                                </div>
                                                <p className="text-muted-foreground text-sm">
                                                    {formatDate(booking.start_date)} — {formatDate(booking.end_date)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-mono text-lg font-bold text-primary">${(booking.total_cost || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    Deposit: ${(booking.deposit_amount || 100).toFixed(2)}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default CustomerDashboardPage;
